import {
  AMC,
  COMPANY,
  COMPANY_MEMBER,
  COMPANY_MANAGER,
  SOLE_PROPRIETOR,
  PROPERTY_DATA_COLLECTOR,
  STATE_CREDENTIALS,
  EO_INSURANCE,
  W_9,
  BANK_INFO,
  ADDRESS_COVERAGE,
  CONTRACT_APPRAISER,
  STAFF_APPRAISER,
  CREDENTIALS,
  COMPANY_OR_COVERAGE,
  TAX_AND_INSURANCE,
  PAYMENT,
} from '@components/Signup/constants'
import { notification } from 'antd'
import CompanyOrCoverage from './steps/CompanyOrCoverage/CompanyOrCoverage'
import Credentials from './steps/Credentials/Credentials'
import LegalVerification from './steps/LegalVerification/LegalVerification'
import PaymentStep from './steps/PaymentStep/PaymentStep'

export const companyStaffItems = [STATE_CREDENTIALS, ADDRESS_COVERAGE, EO_INSURANCE, BANK_INFO]

export const companyContractItems = [STATE_CREDENTIALS, ADDRESS_COVERAGE, EO_INSURANCE, W_9, BANK_INFO]

export const companyNonAppraiserItems = [ADDRESS_COVERAGE, EO_INSURANCE, W_9, BANK_INFO]

export const companyMemberStaffItems = [STATE_CREDENTIALS, ADDRESS_COVERAGE]

export const companyMemberContractItems = [STATE_CREDENTIALS, ADDRESS_COVERAGE, EO_INSURANCE, W_9]

export const soleProprietorItems = [STATE_CREDENTIALS, ADDRESS_COVERAGE, EO_INSURANCE, W_9, BANK_INFO]

export const companyManagerNonAppraiserItems = [ADDRESS_COVERAGE]

export const companyManagerStaffItems = companyMemberStaffItems

export const companyManagerContractItems = companyMemberContractItems

export const AMCItems = companyNonAppraiserItems

export const getRequiredItems = ({ userType, isAppraiser, appraiserType }) => {
  if (userType === AMC) {
    return AMCItems
  }
  if (userType === SOLE_PROPRIETOR) {
    return soleProprietorItems
  }
  if (userType === COMPANY && !isAppraiser) {
    return companyNonAppraiserItems
  }
  if (userType === COMPANY && appraiserType === STAFF_APPRAISER) {
    return companyStaffItems
  }
  if (userType === COMPANY && appraiserType === CONTRACT_APPRAISER) {
    return companyContractItems
  }
  if (userType === COMPANY_MEMBER && appraiserType === STAFF_APPRAISER) {
    return companyMemberStaffItems
  }
  if (userType === COMPANY_MEMBER && appraiserType === CONTRACT_APPRAISER) {
    return companyMemberContractItems
  }
  if (userType === COMPANY_MANAGER && !isAppraiser) {
    return companyManagerNonAppraiserItems
  }
  if (userType === COMPANY_MANAGER && appraiserType === STAFF_APPRAISER) {
    return companyManagerStaffItems
  }
  if (userType === COMPANY_MANAGER && appraiserType === CONTRACT_APPRAISER) {
    return companyManagerContractItems
  }
  // If a user is missing fields (old invite), assume company admin as that is what
  // all invitations were generated as previously.
  return companyStaffItems
}

export const companyAppraiserSteps = [CREDENTIALS, COMPANY_OR_COVERAGE, TAX_AND_INSURANCE, PAYMENT]

export const companyNonAppraiserSteps = [COMPANY_OR_COVERAGE, TAX_AND_INSURANCE, PAYMENT]

export const companyMemberStaffSteps = [CREDENTIALS, COMPANY_OR_COVERAGE]

export const companyMemberContractSteps = [CREDENTIALS, COMPANY_OR_COVERAGE, TAX_AND_INSURANCE]

export const soleProprietorSteps = companyAppraiserSteps

export const AMCSteps = companyNonAppraiserSteps

export const companyManagerNonAppraiserSteps = [COMPANY_OR_COVERAGE]

export const companyManagerStaffSteps = companyMemberStaffSteps

export const companyManagerContractSteps = companyMemberContractSteps

const availableSteps = {
  [CREDENTIALS]: {
    Component: Credentials,
    progressTrackerTitle: 'Credentials',
  },
  [COMPANY_OR_COVERAGE]: {
    Component: CompanyOrCoverage,
    progressTrackerTitle: (userType) => (userType === COMPANY || userType === AMC ? 'Company' : 'Coverage'),
    contentClassName: 'content content-wide',
  },
  [TAX_AND_INSURANCE]: {
    Component: LegalVerification,
    progressTrackerTitle: 'Legal Verification',
  },
  [PAYMENT]: {
    Component: PaymentStep,
    progressTrackerTitle: 'Payment',
  },
}

const getSteps = ({ userType, isAppraiser, appraiserType }) => {
  if (userType === AMC) {
    return AMCSteps
  }
  if (userType === SOLE_PROPRIETOR) {
    return soleProprietorSteps
  }
  if (userType === COMPANY && !isAppraiser) {
    return companyNonAppraiserSteps
  }
  if (userType === COMPANY && isAppraiser) {
    return companyAppraiserSteps
  }
  if (userType === COMPANY_MEMBER && appraiserType === STAFF_APPRAISER) {
    return companyMemberStaffSteps
  }
  if (userType === COMPANY_MEMBER && appraiserType === CONTRACT_APPRAISER) {
    return companyMemberContractSteps
  }
  if (userType === COMPANY_MANAGER && !isAppraiser) {
    return companyManagerNonAppraiserSteps
  }
  if (userType === COMPANY_MANAGER && appraiserType === STAFF_APPRAISER) {
    return companyManagerStaffSteps
  }
  if (userType === COMPANY_MANAGER && appraiserType === CONTRACT_APPRAISER) {
    return companyManagerContractSteps
  }
  if (userType === PROPERTY_DATA_COLLECTOR) {
    return []
  } // PDC users complete everything in CreateAccount
  // If a user is missing fields (old invite), assume company admin as that is what
  // all invitations were generated as previously
  return companyAppraiserSteps
}

export const buildStepMapAndSectionMap = ({ userType, isAppraiser, appraiserType, initialValues = {} }) => {
  const steps = getSteps({ userType, isAppraiser, appraiserType })
  const stepMap = steps.reduce((acc, currentStepKey, currentIndex) => {
    const { progressTrackerTitle, contentClassName = 'content' } = availableSteps[currentStepKey]
    return {
      ...acc,
      [currentStepKey]: {
        ...availableSteps[currentStepKey],
        nextStep: currentIndex === steps.length - 1 ? null : steps[currentIndex + 1],
        nextStepName: currentIndex === steps.length - 1 ? 'Finish Setup' : 'Continue',
        previousStep: currentIndex === 0 ? null : steps[currentIndex - 1],
        componentProps: {
          userType,
          isAppraiser,
          appraiserType,
          initialValues,
        },
        progressTrackerTitle:
          typeof progressTrackerTitle === 'function' ? progressTrackerTitle(userType) : progressTrackerTitle,
        contentClassName,
      },
    }
  }, {})

  const sectionMap = Object.keys(stepMap).reduce(
    (acc, currentStepKey) => [
      ...acc,
      { header: stepMap[currentStepKey].progressTrackerTitle, steps: [currentStepKey] },
    ],
    [],
  )

  const initialStepName = steps[0]

  return {
    stepMap,
    sectionMap,
    initialStepName,
  }
}

// Create a nested object from formValues, only works for 1 level deep
export const buildNestedFormObject = (formValues) =>
  Object.keys(formValues).reduce((acc, currentKey) => {
    const splitKey = currentKey.split('.')
    if (splitKey.length === 1) {
      return {
        ...acc,
        [currentKey]: formValues[currentKey],
      }
    } else {
      const [keyOne, keyTwo] = splitKey
      const nestedObject = acc[keyOne] || {}
      return {
        ...acc,
        [keyOne]: {
          ...nestedObject,
          [keyTwo]: formValues[currentKey],
        },
      }
    }
  }, {})

export const buildCompanyData = ({ formValues, companyMemberIndices, coverage, isSoleProprietor }) => {
  // Gather valid company members to invite from form data
  const invitations = Object.keys(companyMemberIndices).reduce((acc, _, index) => {
    const email = formValues[`email_${index}`]
    const role = formValues[`role_${index}`]
    return email && role ? [...acc, { recipient_email: email, role: [role] }] : acc
  }, [])

  const { address, city, state, zip, phone, phone_type } = formValues
  return {
    companyData: {
      address,
      city,
      state,
      zip,
      company_coverage: coverage,
      phone,
      phone_type,
      // Only update async for company user_type, not sole_proprietor
      update_coverage_async: !isSoleProprietor,
    },
    invitationsData: invitations,
  }
}

export const openErrorNotification = () => {
  notification.error({
    message: 'An Error Occurred',
    description: 'Please try again or contact support.',
    placement: 'topRight',
  })
}
